import { useState, useCallback, useMemo, useEffect } from 'react';

export function useStepper<T extends string | number>(options: { stepsToComplete: T[]; initialStep?: T }) {
  const { stepsToComplete, initialStep } = options;

  const initial = useMemo(() => {
    return initialStep ?? stepsToComplete[0];
  }, [options]);

  useEffect(() => {
    setStepsDone([initial]);
    setStep(initial);
  }, [initial]);

  const [stepsDone, setStepsDone] = useState<T[]>([initial]);
  const [step, setStep] = useState<T>(initial);

  const getNextStep = useCallback(
    (done: T[]): T | undefined => {
      return stepsToComplete.find(s => !done.includes(s));
    },
    [stepsToComplete],
  );

  const goToNextStep = useCallback(() => {
    setStepsDone(prev => {
      const next = getNextStep(prev);
      if (!next) return prev;
      setStep(next);
      return [...prev, next];
    });
  }, [getNextStep]);

  const goBack = useCallback(() => {
    setStepsDone(prev => {
      const newDone = prev.slice(0, -1);
      const next = newDone[newDone.length - 1] ?? stepsToComplete[0];
      setStep(next);
      return newDone;
    });
  }, [stepsToComplete]);

  const reset = useCallback(() => {
    setStepsDone([initial]);
    setStep(initial);
  }, [initial]);

  return {
    step,
    stepsDone,
    goToNextStep,
    goBack,
    reset,
    isFirstStep: step === stepsToComplete[0],
    isLastStep: step === stepsToComplete[stepsToComplete.length - 1],
  };
}
